/**
 * SearchableSelect
 * A combobox-style select with search for large option lists.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, KeyboardEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

import { useScrollIsolation } from './useScrollIsolation';

export interface SearchableSelectOption<T = string | number> {
  value: T;
  /** Used for trigger display and search/filter. */
  label: string;
  /** Optional rich content for dropdown list (e.g. badges). Falls back to label when absent. */
  optionContent?: ReactNode;
  /** Compact node for the CLOSED trigger when selected — for options whose
   *  optionContent is too tall for the trigger (multi-line cards). Falls back
   *  to optionContent, then label. */
  triggerContent?: ReactNode;
}

export interface SearchableSelectProps<T = string | number> {
  options: SearchableSelectOption<T>[];
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  /** Renders inside the popover header (border-b p-2), e.g. tag filter. */
  headerSlot?: ReactNode;
  /** Creatable: when the typed search matches no option label exactly, an
   *  "Add “…”" row is offered above the matches; picking it calls this with
   *  the trimmed text (the caller creates the option and selects it). */
  onCreate?: (label: string) => void;
}

export function SearchableSelect<T extends string | number>({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  className,
  headerSlot,
  onCreate,
}: SearchableSelectProps<T>): ReturnType<FC<SearchableSelectProps<T>>> {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  // The typed text is offered as a new option only when no label equals it.
  const creatable = search.trim();
  const canCreate =
    !!onCreate &&
    creatable.length > 0 &&
    !options.some((o) => o.label.toLowerCase() === creatable.toLowerCase());
  const handleCreate = useCallback(() => {
    onCreate?.(creatable);
    setSearch('');
    setOpen(false);
  }, [onCreate, creatable]);

  // Allow the options list to scroll even when rendered inside a Radix
  // Dialog/Drawer (whose modal scroll-lock would otherwise cancel the wheel).
  const setListRef = useScrollIsolation(listRef);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label ?? placeholder;

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase().trim();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const handleSelect = useCallback(
    (option: SearchableSelectOption<T>) => {
      onChange(option.value);
      setSearch('');
      setOpen(false);
    },
    [onChange],
  );

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  }, []);

  useEffect(() => {
    if (!open) return;
    setSearch('');
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!open) return;
      const items = filteredOptions;
      const currentIndex = items.findIndex((o) => o.value === value);
      let nextIndex = currentIndex;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = Math.min(currentIndex + 1, items.length - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = Math.max(currentIndex - 1, 0);
      } else if (e.key === 'Enter' && items[nextIndex]) {
        e.preventDefault();
        handleSelect(items[nextIndex]);
        return;
      } else if (e.key === 'Escape') {
        setOpen(false);
        return;
      }

      if (nextIndex >= 0 && items[nextIndex]) {
        onChange(items[nextIndex].value);
        listRef.current?.children[nextIndex]?.scrollIntoView({ block: 'nearest' });
      }
    },
    [open, filteredOptions, value, onChange, handleSelect],
  );

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={placeholder}
          title={selectedOption?.label}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 overflow-hidden rounded-lg border border-input bg-background px-3 text-left text-sm',
            'transition-colors placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !selectedOption && 'text-muted-foreground',
            className,
          )}
        >
          {/* Two render paths because they clip differently. Plain text needs
              a BLOCK wrapper — text-overflow only ellipsizes inline text, so a
              flex wrapper hard-clipped long labels mid-letter. Rich content
              (option/triggerContent) needs the opposite: a flex row that
              FORCES every descendant single-line — dropdown cards use
              flex-wrap, and a wrapped second row inside the h-10 trigger gets
              vertically chopped by overflow-hidden. */}
          {selectedOption?.triggerContent ?? selectedOption?.optionContent ? (
            <span className="flex h-full min-w-0 flex-1 items-center overflow-hidden [&_*]:!flex-nowrap [&_*]:!whitespace-nowrap">
              {selectedOption.triggerContent ?? selectedOption.optionContent}
            </span>
          ) : (
            <span className="block min-w-0 flex-1 truncate">{displayLabel}</span>
          )}
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className={cn(
            // The cap follows a WIDER trigger (e.g. the wizard's 29rem rule
            // picker) so long labels don't wrap; narrow triggers keep the
            // 360px cap they always had.
            'z-[250] w-[max(320px,var(--radix-popover-trigger-width))] min-w-[320px] max-w-[max(360px,var(--radix-popover-trigger-width))] rounded-lg border border-border bg-popover p-0 text-popover-foreground shadow-md',
            // Open animation ONLY. An exit animation makes Radix keep the
            // closed layer mounted until animationend — and when that event
            // never fires, the invisible-but-opaque layer swallows every click
            // under it (the MD-1030 "ghost overlay"). Same rule applies to the
            // other popover inputs.
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="border-b border-border p-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  type="search"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  autoFocus
                  autoComplete="off"
                />
              </div>
              {headerSlot}
            </div>
          </div>
          <div
            ref={setListRef}
            role="listbox"
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            className="max-h-[320px] overflow-y-auto p-2"
          >
            {canCreate && (
              <button
                type="button"
                className="mb-1 w-full rounded-md border border-dashed border-border px-2 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                onClick={handleCreate}
              >
                Add “{creatable}”
              </button>
            )}
            {filteredOptions.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {options.length === 0 ? 'No options' : 'No matches'}
              </p>
            ) : (
              filteredOptions.map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  role="option"
                  aria-selected={opt.value === value}
                  className={cn(
                    'w-full rounded-md px-2 py-2 text-left text-sm transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    opt.value === value && 'bg-accent text-accent-foreground',
                  )}
                  onClick={() => handleSelect(opt)}
                >
                  {opt.optionContent ?? opt.label}
                </button>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default SearchableSelect;
